import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type FC, type ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useGame } from '@/hooks/useGame';
import { api, interceptorAddGameAndSandbox } from '@/lib/api-client';
import {
  SANDBOX_LOCAL_STORAGE,
  sandboxCode,
  storeGameSandbox,
} from '@/lib/utils';

interface SandboxContextType {
  selectedSandbox: string;
  setSelectedSandbox: (sandbox: string) => void;
  gameCode: string;
}

const SandboxContext = createContext<SandboxContextType | undefined>(undefined);

interface SandboxProviderProps {
  children: ReactNode;
  initialSandbox?: string;
}

export const SandboxProvider: FC<SandboxProviderProps> = ({
  children,
  initialSandbox,
}) => {
  const game = useGame();
  const gameCode = game.game_code;
  const queryClient = useQueryClient();

  //Track if the sandbox is set from a storage event
  const isFromStorageEvent = useRef(false);

  const [selectedSandbox, setSelectedSandboxState] = useState<string>(() => {
    // Initialize sandbox from URL param if provided, otherwise from localStorage
    if (initialSandbox) {
      return initialSandbox;
    }
    const storedSandbox = sandboxCode(gameCode);
    return storedSandbox;
  });

  // Custom setter that also handles localStorage and interceptor logic
  const setSelectedSandbox = (sandbox: string) => {
    setSelectedSandboxState(sandbox);
  };

  // Set up interceptor synchronously before any renders can trigger API calls
  useLayoutEffect(() => {
    // Add new interceptor
    const interceptor = interceptorAddGameAndSandbox(gameCode, selectedSandbox);

    // Only store the sandbox if it is not from another window's storage event
    if (!isFromStorageEvent.current) {
      storeGameSandbox(gameCode, selectedSandbox);
    }

    // Invalidate queries when sandbox changes (but not games/sandboxes queries)
    void queryClient.invalidateQueries({
      predicate: (query) =>
        !query.queryKey.includes('sandboxes') &&
        !query.queryKey.includes('games'),
    });

    return () => {
      api.interceptors.request.eject(interceptor);
    };
  }, [gameCode, selectedSandbox]);

  // Listen to localStorage changes (from other tabs/windows)
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SANDBOX_LOCAL_STORAGE && event.newValue) {
        // IMPORTANT: event.newValue contient tout le localStorage
        // Il faut extraire le sandbox pour ce jeu spécifique
        try {
          const sandboxes = JSON.parse(event.newValue) as {
            game: string;
            sandbox: string;
          }[];
          const currentGameSandbox = sandboxes.find(
            (sandbox) => sandbox.game === gameCode,
          );

          if (
            currentGameSandbox &&
            currentGameSandbox.sandbox !== selectedSandbox
          ) {
            // Marquer que ce changement vient d'un événement storage
            isFromStorageEvent.current = true;
            setSelectedSandboxState(currentGameSandbox.sandbox);
          }
        } catch (e) {
          console.error('Error parsing storage event:', e);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [gameCode, selectedSandbox]);

  const value: SandboxContextType = {
    selectedSandbox,
    setSelectedSandbox,
    gameCode,
  };

  return (
    <SandboxContext.Provider value={value}>{children}</SandboxContext.Provider>
  );
};

export const useSandboxContext = (): SandboxContextType => {
  const context = useContext(SandboxContext);
  if (context === undefined) {
    throw new Error('useSandboxContext must be used within a SandboxProvider');
  }
  return context;
};
